'use client';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { ArrowRight, Plus, Gift } from 'lucide-react';
import { LANDING_CONTENT } from '../constants/content';
import LandingReveal from './LandingReveal';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import WhatsAppDemoForm from './WhatsAppDemoForm';
import MarketingDisclaimer from './MarketingDisclaimer';
// R7 paper flip: the price surface + the form's own card, previously the
// house `Surface` recipe (dark-hardcoded, no paper variant), flatten to a
// plain hairline-ruled `PAPER_BAND.base`/`PAPER_RULE.hairline` div instead
// (design contract "NO card-in-card: flatten any nested surface" — same
// move PricingSection.tsx's TierCard makes). `DARK_BAND` stays imported for
// the phone/chat exhibit ONLY — HeroChatReveal is a dark product-recreation
// figure that survives R7 verbatim (design contract decision 4: "dark
// exhibits" — real dark app on display, framed, never page chrome).
import { PAPER_BAND, PAPER_INK, PAPER_RULE } from '@/features/marketing/ui/paper-tokens';
import { DARK_BAND, DARK_INK } from '@/features/marketing/ui/dark-tokens';
import { LANDING_PRIMARY_CTA_RECIPE } from '@/features/marketing/ui/cta-recipe';

// Chat demo island — ssr:false keeps this client-only widget out of the
// initial RSC stream (below-fold, never the LCP element). The loading
// fallback is a same-size, same-band skeleton matching HeroChatReveal's own
// OUTER stage box exactly (D §1.4.3-4 "same-size phone skeleton as the
// dynamic fallback") — the reserved geometry never changes between skeleton,
// hydration, and the live banner/phone, so there is zero CLS at any step.
// 2026-07-12 R3.5 (figure specs §4.3 "New shell dims"): the phone shell
// shrank from a mostly-empty 3:1 shell to a ~2.15:1 shell with real WhatsApp
// chrome (HeroChatReveal.tsx). The skeleton dims MUST match the live shell
// exactly at every breakpoint — this is a CLS-linked triple, not a cosmetic
// tweak.
const HeroChatReveal = dynamic(() => import('./HeroChatReveal'), {
  ssr: false,
  loading: () => (
    <div
      aria-hidden="true"
      className={cn(
        'w-[300px] h-[648px] md:w-[264px] md:h-[568px] lg:w-[312px] lg:h-[672px] xl:w-[336px] xl:h-[724px] rounded-[28px] lg:rounded-[32px] border border-[#FFFFFF1A]',
        DARK_BAND.bot.bg
      )}
    />
  ),
});

/**
 * SolutionSection — THE single optional-bot chapter. Round-3 rebuild
 * (2026-07-11, R3 judge D verdict "Bot chapter",
 * .claude/handoffs/landing-review-artifacts/r3-judge-D-verdict.md):
 *
 * 2026-08-12 R8 §W (`.claude/handoffs/2026-08-12-r8-restructure-contract.md`):
 * SHELL-ONLY reframe as the page's "optional module" chapter, moved BELOW the
 * new Setup section (Alex amendment — was directly after Pricing). Changed:
 * the eyebrow pill now reads `whatsappModule.badge` ("OPTIONAL MODULE") in
 * place of `botSection.eyebrow`; the H2/sub read `whatsappModule.h2`/`.body`
 * in place of `botSection.headline`/`.sub`; the module-activation CTA's TEXT
 * reads `whatsappModule.cta` in place of `botSection.moduleInfo.cta` (href
 * stays the canonical `/auth/register`, unchanged mechanism); the phone
 * exhibit's wrapping mat gains a dark app-chrome titlebar ("dark flow-builder
 * frame header" per the contract) reading `whatsappModule.frameTitle`/
 * `.frameTag`, so the exhibit reads as a real product screen (a Flow Builder
 * window), not just a bare phone. Everything else is BYTE-IDENTICAL: the
 * `HeroChatReveal`/`WhatsAppDemoForm` mounts, the disclaimer, the derived
 * price surface (still `botSection.priceSurface.*` — commercial terms, not
 * part of this reframe), and the flag-off register fallback. Root band stays
 * `PAPER_BAND.alt`; interior cards (price surface, form wrapper) stay
 * `PAPER_BAND.base` — unchanged, per the contract's "keep interior base-fill
 * card contrast" instruction.
 *
 * 2026-08-12 R8 review FIX 4 — the full-width "config together" chapter that
 * closed this section (headline + supporting line + a ruled 4-step grid,
 * `configTogether.*`) is DELETED, and with it the last consumer of that
 * content key (the key itself is removed from content.ts in the same change:
 * expand-before-contract does not apply when the consumer deletion lands
 * together with it). The R8 pass had kept it on the reasoning that "configure
 * it together" is bot-specific rather than a duplicate of §S Setup — on the
 * assembled page that reasoning did not survive: SetupSection now argues
 * exactly "you do not receive an empty account, we configure it around your
 * process" as its whole thesis, and this grid put a THIRD numbered 4-step
 * strip on one page (OneProcess §P, Setup §S, and this) — three sequences
 * sharing one fingerprint, so none of them reads as the page's spine. Nothing
 * true is lost: the bot-specific half of the claim already ships verbatim in
 * this section's own sub-line ("We configure it together with you." /
 * "Îl configurăm împreună cu tine.", `whatsappModule.body`).
 *
 * Row 1 — money-shot phone (via HeroChatReveal: iOS notification -> WhatsApp
 * conversation morph) LEFT + module activation CTA beneath it; RIGHT track is
 * a self-centered, non-stretching stack of: the "bot separate from CRM"
 * disclaimer (via E's `MarketingDisclaimer` `icon="module" placement=
 * "floating"`, per the R3 rulings amendment), a derived commercial price
 * surface, and — flag-gated — the real `WhatsAppDemoForm` (staged flow, see
 * R5-P3 note below), or (flag off) the existing register fallback.
 * `NEXT_PUBLIC_WHATSAPP_DEMO_ENABLED` gates ONLY the test-path promise; the
 * disclaimer and price surface are commercial terms, not a live-test claim,
 * so they always render (D §1.6.6 "never advertise a live test above a
 * disabled form" — the honesty boundary is the promise, not the terms).
 *
 * R5-P3 LANE L5 (design amendment §6 "BOT — staged interactive flow",
 * Decision 9): the three instructional "test now" step cards + their
 * connector spine + `TEST_STEP_ICONS` are DELETED — Alex: "nu e nevoie
 * să-mi explici pașii... Nu mă înnebuni." `WhatsAppDemoForm` now renders
 * `staged`, replacing the explainer with its own 3-step CTA-card/name+phone/
 * language+consent+send machine over the identical fields/API. H2 + the
 * config-together H3 below convert to the shared `landing-serif-display`
 * type system (§1).
 *
 * The former Row 2 (half-width config card + half-width amber module card) is
 * DELETED (D §1.8 "true full-width chapter"): the setup story now renders as
 * a direct child of `section#bot`, outside the capped row wrapper, full
 * viewport width with its own top/bottom hairlines; commercial terms moved
 * into Row 1's price surface above.
 *
 * The incoming Pricing->Bot hairline is DELETED (previously
 * `border-t border-[#FFFFFF12]` on the section root): the Pricing->Bot seam
 * is now owned by F's interleaved `DisclaimerDivider` (LandingPage.tsx),
 * composing E's `MarketingDisclaimer` — rendering a second line here would
 * double the alpha read at the boundary (r3-debate-rulings.md Harm. b).
 *
 * R4-P3 (masterplan §2.5 "Bot"): every hand-rolled card recipe in this
 * section (price surface, the form's own card) now composes the house
 * `Surface` primitive (`ui/Surface.tsx`) instead of a bespoke rounded/
 * border/bg/shadow triple, for cross-figure consistency with every other
 * P2-primitive-built mock on the page. (The 3 step cards + their bespoke
 * connector spine this paragraph used to describe are the R5-P3 deletion
 * above — the SVG stroke recipe never outlived the cards it connected.)
 *
 * Related: .claude/handoffs/2026-07-11-landing-round3-masterplan.md;
 * .claude/handoffs/landing-review-artifacts/r3-judge-D-verdict.md;
 * .claude/handoffs/landing-review-artifacts/r4/r4-p3-masterplan.md §2.5.
 */
function BotSectionInner() {
  const { botSection, nav, whatsappModule } = LANDING_CONTENT;
  const { t } = useLocale();

  // Flag gates ONLY the live-test promise (the staged form); the disclaimer +
  // price surface below always render (they are commercial terms, not a
  // "botul îți scrie" test-path claim).
  const demoEnabled = process.env.NEXT_PUBLIC_WHATSAPP_DEMO_ENABLED === 'true';

  return (
    // R6 rhythm (judge §2.9, three-beat interlude-to-Bot handoff): eases
    // between Pricing's compression and the section below via py-20 lg:py-24
    // (was py-24 lg:py-32 = 96/128px, the R5-P3 FAQ/Trust/FinalCTA-matched
    // pair -- superseded this round; Bot's beat is "eases between," not the
    // shared 96->128px conformance rhythm anymore).
    <section id="bot" className="relative py-20 lg:py-24 overflow-hidden scroll-mt-24">
      {/* R7 paper flip: Bot chapter band -> PAPER_BAND.alt (was locked dark
          neutral ramp, DARK_BAND.bot). The prior static ambient glow (a
          soft white radial wash tuned for a dark surface) is DROPPED
          outright, not converted — the hallmark editorial genre bans
          atmospheric glow FX on page chrome (no registry token exists for
          it, and it has no role once the section is flat paper); amber
          atmosphere stays reserved for the two conversion poles regardless. */}
      <div className={cn('absolute inset-0', PAPER_BAND.alt.bg)} aria-hidden="true" />

      <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* ── Header — left-aligned. ── */}
          <LandingReveal>
            <div className="max-w-[52ch] mb-8 lg:mb-12">
              <div
                className={cn(
                  'inline-flex items-center h-7 px-3 rounded-full border mb-5',
                  PAPER_RULE.hairline.border,
                  'bg-black/[0.03]'
                )}
              >
                {/* R8 §W: reads `whatsappModule.badge` ("OPTIONAL MODULE") —
                    was `botSection.eyebrow` pre-restructure. Same pill chrome,
                    new copy source (SHELL-only reframe, see file header). */}
                <span
                  className={cn(
                    'text-[11px] leading-4 font-semibold uppercase tracking-[0.16em]',
                    PAPER_INK.muted.text
                  )}
                >
                  {t(whatsappModule.badge)}
                </span>
              </div>
              {/* R5-P3 LANE L5 (design amendment §1 "the serif display law" +
                  §7 conformance matrix "Bot | H2 serif 40"): family swap +
                  34->40px ramp via the shared `landing-serif-display` utility
                  (globals.css) — drops the old Inter font-semibold/tight-
                  tracking treatment, same drop-in-the-same-edit pattern every
                  other converted section H2 uses (FeatureGridSection.tsx,
                  HeroSection.tsx's H1, FAQSection.tsx, TrustBlockSection.tsx,
                  FinalCTASection.tsx).
                  R8 §W: reads `whatsappModule.h2`/`.body` — was
                  `botSection.headline`/`.sub` pre-restructure. */}
              <h2
                className={cn(
                  'landing-serif-display mb-5 max-w-[18ch] text-[34px] leading-[1.15] lg:mb-6 lg:text-[40px] lg:leading-[1.1]',
                  PAPER_INK.primary.text
                )}
              >
                {t(whatsappModule.h2)}
              </h2>
              <p
                className={cn(
                  'text-lg lg:text-xl leading-[1.55] tracking-[-0.01em] font-normal max-w-[52ch]',
                  PAPER_INK.secondary.text
                )}
              >
                {t(whatsappModule.body)}
              </p>
            </div>
          </LandingReveal>
        </div>

        {/* ── Row 1 — phone (money shot) + activation CTA LEFT; disclaimer +
            price + test path/form RIGHT. Own container, wider than the header's
            max-w-6xl at lg/xl per the viewport table (D §2.F). ── */}
        <div className="max-w-6xl lg:max-w-[1280px] xl:max-w-[1440px] mx-auto">
          <div
            className={cn(
              'flex flex-col gap-12',
              'md:grid md:grid-cols-[256px_minmax(0,1fr)] md:gap-6 md:items-start',
              'lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-10',
              'xl:grid-cols-[minmax(0,1fr)_480px] xl:gap-12'
            )}
          >
            {/* Left track — the phone owns its own entrance (in-view scene
                trigger inside HeroChatReveal itself); no pair-level reveal
                wraps this column (D §1.4.3.iii — the money-shot object must
                own its own entrance). */}
            <div className="flex flex-col items-center gap-6">
              {/* R7 paper flip: "framed dark exhibit" (design contract
                  decision 4/enrichment law) — HeroChatReveal is a real
                  dark-app product recreation that survives R7 verbatim
                  (out of L3's file ownership), so instead of forking it,
                  this wraps its render site in a hairline paper mat —
                  nothing dark paints naked directly on the page anymore.
                  Wraps BOTH the loading skeleton and the hydrated phone
                  identically (Next.js `dynamic()` swaps content in-place at
                  this exact JSX position), and the mat's own box (border +
                  static padding) is size-stable before/after hydration, so
                  it adds zero CLS risk on top of the existing same-size-
                  skeleton contract. */}
              <div
                data-figure-role="exhibit-frame"
                className={cn(
                  'rounded-[32px] border p-2 lg:rounded-[36px] lg:p-3',
                  PAPER_RULE.hairline.border,
                  PAPER_BAND.base.bg
                )}
              >
                {/* R8 §W: "dark flow-builder frame header" — an app-chrome
                    titlebar (product name + tag) sitting atop the phone,
                    INSIDE the same paper mat, so the exhibit reads as a real
                    product window (a Flow Builder screen) rather than a bare
                    phone. Renders unconditionally above the dynamic-import
                    slot, so it is present identically for BOTH the loading
                    skeleton and the hydrated phone — zero CLS risk on top of
                    the existing same-size-skeleton contract (see the R7 note
                    above). Dark fill is DARK_BAND.bot — the same dark-exhibit
                    tone the phone/chat itself already carries — never a raw
                    hex; ink is DARK_INK (bright/tertiary), the dark-exhibit
                    ink ladder, per the "DARK_* inside exhibit interiors only"
                    law. */}
                {/* 2026-08-13 sibling audit of the RO campaign-card bug: this
                    header carries the SAME anatomy (a `truncate` title beside a
                    `shrink-0` tag that cannot yield) and the same RO exposure —
                    "CMG WhatsApp Flow Builder" + "modul opțional" need more
                    width than the phone mat has at its 300px base and 264px md
                    shells, so the product name was the one that crushed. Same
                    fix as `DifferentiatorsSection`'s CampaignCard: wrap instead
                    of starve. Size-stable across the dynamic-import swap either
                    way (wrapping is width-driven, not hydration-driven), so the
                    zero-CLS contract documented above is untouched. */}
                <div
                  className={cn(
                    'mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-[20px] px-4 py-2.5 lg:rounded-[24px]',
                    DARK_BAND.bot.bg
                  )}
                >
                  <span className={cn('truncate text-[13px] font-semibold', DARK_INK.bright.text)}>
                    {t(whatsappModule.frameTitle)}
                  </span>
                  <span
                    className={cn(
                      'shrink-0 text-[11px] uppercase tracking-[0.08em]',
                      DARK_INK.tertiary.text
                    )}
                  >
                    {t(whatsappModule.frameTag)}
                  </span>
                </div>
                <HeroChatReveal />
              </div>
              {/* Module activation CTA — 44px, fit-content, moved directly
                  beneath the chat (D §1.5.4). Never a second full-width bar.
                  R7: adopts LANDING_PRIMARY_CTA_RECIPE wholesale instead of a
                  hand-typed amber copy (reuse-before-extend) — its
                  outline-based focus needs no band-specific ring-offset
                  token, so the DARK_VARIANT.botRingOffset dependency this
                  CTA previously carried drops entirely, not just its color.
                  R8 §W: TEXT reads `whatsappModule.cta` — was
                  `botSection.moduleInfo.cta` pre-restructure; href/mechanism
                  unchanged (still the canonical `/auth/register` route). */}
              <Link
                href="/auth/register"
                prefetch={false}
                className={cn(
                  LANDING_PRIMARY_CTA_RECIPE,
                  'inline-flex w-fit items-center justify-center h-11 px-5 rounded-xl text-[15px] font-semibold'
                )}
              >
                {t(whatsappModule.cta)}
              </Link>
            </div>

            {/* Right track — self-centered, never stretched to the phone's
                row height (D §1.4.3.ii). */}
            <LandingReveal delay={100}>
              <div className="flex flex-col gap-5 lg:gap-6 md:self-center">
                {/* Disclaimer — E's MarketingDisclaimer, floating placement,
                    module icon (rulings amendment index #8; E cross-lane note
                    "Judge D uses MarketingDisclaimer with floating placement
                    and module icon"). Copy reuses the existing canonical
                    BOT_MODULE_HEADLINE (botSection.moduleInfo.headline) — the
                    same "Botul WhatsApp este separat de CRM." sentence, one
                    owner, zero drift. */}
                <MarketingDisclaimer
                  copy={{ lead: botSection.moduleInfo.headline }}
                  icon="module"
                  placement="floating"
                />

                {/* Derived commercial price surface (D §1.7.2). Values
                    template through PLAN_FACTS.bot in content.ts, same as the
                    existing botSection.moduleInfo.terms constants (D §1.7.3
                    "do not replace the template expressions... with numeric
                    literals"). R7 paper flip: the house `Surface` recipe
                    (dark-hardcoded) flattens to a plain hairline paper card
                    — mono-label eyebrow + a ruled `dl` (icon dt + text dd,
                    structure not new fake labels — §0 LAW) instead of two
                    faint lines. */}
                <div
                  data-stroke-role="card"
                  className={cn(
                    'rounded-[12px] border p-5',
                    PAPER_RULE.hairline.border,
                    PAPER_BAND.base.bg
                  )}
                >
                  <span
                    className={cn(
                      'text-[11px] leading-4 font-semibold uppercase tracking-[0.14em]',
                      PAPER_INK.muted.text
                    )}
                  >
                    {t(botSection.priceSurface.eyebrow)}
                  </span>
                  <p
                    className={cn(
                      'mt-2 text-[28px] font-[650] tabular-nums',
                      PAPER_INK.primary.text
                    )}
                  >
                    {t(botSection.priceSurface.amount)}
                  </p>
                  <p className={cn('mt-1 text-[13px]', PAPER_INK.secondary.text)}>
                    {t(botSection.priceSurface.included)}
                  </p>
                  <dl className={cn('mt-3 space-y-2 border-t pt-3', PAPER_RULE.soft.border)}>
                    <div className="flex items-center gap-2">
                      <dt aria-hidden="true" className="flex shrink-0 items-center">
                        <Plus className={cn('h-3.5 w-3.5', PAPER_INK.muted.text)} />
                      </dt>
                      <dd className={cn('text-[12px]', PAPER_INK.muted.text)}>
                        {t(botSection.priceSurface.overage)}
                      </dd>
                    </div>
                    <div className="flex items-center gap-2">
                      <dt aria-hidden="true" className="flex shrink-0 items-center">
                        <Gift className={cn('h-3.5 w-3.5', PAPER_INK.muted.text)} />
                      </dt>
                      <dd className={cn('text-[12px]', PAPER_INK.muted.text)}>
                        {t(botSection.priceSurface.trial)}
                      </dd>
                    </div>
                  </dl>
                </div>

                {demoEnabled ? (
                  // R5-P3 LANE L5 (design amendment §6, Decision 9): the 3
                  // instructional step cards + connector spine that used to
                  // render here are DELETED — WhatsAppDemoForm's own `staged`
                  // flow (CTA card -> name+phone -> language+consent+send)
                  // replaces the explainer with the real form's own steps.
                  // Still in its own card surface (D §1.6.5) — R7 paper
                  // flip: flattens from the dark `Surface` recipe to the
                  // same plain hairline paper card as the price surface
                  // above (sibling flatten, not nested — both are top-level
                  // children of this column, so "no card-in-card" was never
                  // violated by having two; the move is because Surface
                  // itself has no paper variant).
                  <div
                    data-stroke-role="card"
                    className={cn(
                      'rounded-[12px] border p-5 sm:p-6',
                      PAPER_RULE.hairline.border,
                      PAPER_BAND.base.bg
                    )}
                  >
                    <WhatsAppDemoForm staged />
                  </div>
                ) : (
                  // Flag off → the existing register fallback (never advertise
                  // a live test above a disabled form).
                  <Link
                    href="/auth/register"
                    prefetch={false}
                    className={cn(
                      LANDING_PRIMARY_CTA_RECIPE,
                      'w-full inline-flex items-center justify-center gap-2 h-12 px-5 sm:px-6 rounded-xl text-[15px] font-semibold'
                    )}
                  >
                    <span>{t(nav.cta.primary)}</span>
                    <ArrowRight className="w-4 h-4 shrink-0" aria-hidden="true" />
                  </Link>
                )}
              </div>
            </LandingReveal>
          </div>
        </div>
      </div>

      {/* The full-width "config together" chapter that used to close this
          section is DELETED (R8 review FIX 4 — see the file header for why
          and for where its argument now lives). */}
    </section>
  );
}

export default function SolutionSection() {
  return <BotSectionInner />;
}
